/**
 * 좌 검색 패널 — PROVE(기본) / 관세율표 lookup / 다중검색.
 *
 * 검색 탭 흐름:
 *  1. 자유 검색 query → /api/onto/tariff-search (키워드+의미 fallback)
 *  2. distinct chapter chip(매칭 류) + 번역 hint + 매칭어휘 표시
 *  3. 숫자 입력(2/4/6/10자리)은 자동 navigate. 키워드는 결과 view 펼침.
 *  ※ PROVE는 중앙 작업공간에서 최신 검수 결정트리와 XYFlow 근거를 표시.
 *
 * Props:
 *  - currentChapter: 현재 보고 있는 chapter (highlight 용)
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import { isVerdictTabVisible } from "./verdictTab";
import { Search, Network, Settings2 } from "lucide-react";
import { CHAPTERS_97 } from "./chaptersData";
import RichInvoiceGrid from "../components/RichInvoiceGrid";
import { highlightTokens } from "./highlightTokens";
import { useAuditSearch } from "./AuditSearchContext";
import { fetchPrecedentHints, fetchSubdivisionPreview } from "./subdivisionPreviewClient";
import { loadTreeFacts } from "./treeFactStore";
import { userAuthHeaders } from "./userAuth";

function chapterName(ch) {
  const found = CHAPTERS_97.find((c) => c.ch === ch);
  return found ? found.name : "";
}

// 검색 옵션 토글 chip (확장 / 의미 / 관세사). active 시 테두리 + 텍스트만 강조.
// 안쪽 채움 제거 (회색톤 절제) — 시각 잡음 최소화.
function SearchOptToggle({
  label,
  checked,
  onChange,
  activeBorder,
  activeColor,
  title,
  hint,
}) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        fontSize: 12,
        color: checked
          ? activeColor || "var(--audit-text)"
          : "var(--audit-text-muted)",
        background: "var(--audit-bg)",
        border: "1px solid " + (checked ? activeBorder : "var(--audit-border)"),
        borderRadius: 4,
        cursor: "pointer",
        userSelect: "none",
        fontWeight: checked ? 600 : 400,
      }}
      title={title}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ margin: 0, cursor: "pointer" }}
      />
      <span>
        {label}
        {hint ? (
          <span
            style={{
              marginLeft: 4,
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 12,
              color: "var(--audit-text-faint)",
              fontWeight: 400,
            }}
          >
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
}

// search mode toggle — 항상 펼친 inline tabs.
function SearchModeToggle({ mode, setMode, modes }) {
  return (
    <div
      style={{
        alignSelf: "flex-start",
        display: "inline-flex",
        gap: 0,
        padding: "1px 4px",
        background: "var(--audit-bg-hover)",
        borderRadius: 4,
      }}
    >
      {modes.map((m) => {
        const active = mode === m.id;
        return (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            title={m.title}
            style={{
              padding: "4px 12px",
              fontSize: 12,
              border: "none",
              background: "transparent",
              color: active ? "var(--audit-text)" : "var(--audit-text-muted)",
              borderBottom: `2px solid ${active ? "var(--audit-text)" : "transparent"}`,
              borderRadius: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              fontWeight: active ? 700 : 400,
            }}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

// 관세율표 lookup이 기본이며 PROVE는 사용자가 명시적으로 선택해 실행한다.
const MODES = [
  {
    id: "ai",
    label: "확정 검토",
    title: "자연어 사실추출 → 검수 결정트리 → 근거 그래프로 판정",
  },
  {
    id: "auto",
    label: "관세율표",
    title:
      "키워드 정확 매칭 우선 + 부족 시 의미 보강 자동. 사용자가 mode 신경쓸 필요 없음.",
  },
  {
    id: "batch",
    label: "다중검색",
    title:
      "그리드 기반 다중 keyword 검색 — 행 클릭 시 관세율표 후보와 근거 자료를 함께 갱신",
  },
];

// CielHS 풍 검색어 type 자동 감지 → URL navigate helper.
// 숫자 2자리 = chapter, 4자리 = 호, 10자리 = 세번, 6자리 = 호 fallback.
function navigateAuto({ chapter, hs, hsk, sub }) {
  const u = new URL(window.location.href);
  u.searchParams.set("audit", "onto-detail");
  ["section", "chapter", "hs", "hsk", "sub"].forEach((k) =>
    u.searchParams.delete(k),
  );
  if (chapter) u.searchParams.set("chapter", chapter);
  if (hs) u.searchParams.set("hs", hs);
  if (hsk) u.searchParams.set("hsk", hsk);
  if (sub) u.searchParams.set("sub", sub); // 소호 필터 prefix(예 20094 → 2009.4 만 표시)
  window.history.pushState({}, "", u.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}

// 검색결과 종합 스테이지 — sq 만 남기고 계층 파라미터 제거 (CLHS 5-섹션 뷰).
// 검색 = 항상 종합 뷰 먼저 (2026-07-07 스펙, 첫 호 자동 진입 제거).
function navigateSearchStage(q, tab) {
  const u = new URL(window.location.href);
  u.searchParams.set("audit", "onto-detail");
  ["section", "chapter", "hs", "hsk"].forEach((k) => u.searchParams.delete(k));
  u.searchParams.set("sq", q);
  // tab 명시(예: PROVE 제출 → "ai" 자동 활성). 미지정 시 기존 탭 유지.
  if (tab) u.searchParams.set("tab", tab);
  window.history.pushState({}, "", u.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}

// CielHS 풍 HS 코드 입력 마스크 — 숫자(구분자 허용)만 입력 시 DDDD.DD-DDDD 자동 포맷.
//   4자리 초과 시 '.'(2009.4), 6자리 초과 시 '-'(2009.41-0000). 최대 10자리(세번).
//   문자 포함 키워드(pineapple 등)는 그대로 통과. CAS 번호(대시 수동)는 사용자 몫(주 용도=세번).
export function formatHsInput(raw) {
  if (/[^0-9.\s-]/.test(raw)) return raw; // 문자 포함 → 키워드, 마스킹 안 함
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}.${d.slice(4)}`;
  return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}`;
}

// 매칭 source label — backend fallback type → 사용자 친화 한글.
// "검색매칭 하단" 에 어떤 DB 어휘로 hit 했는지 노출 (사용자 추적용).
const MATCH_SOURCE_LABEL = {
  null: "DB 표준어휘 (관세청 품명·name_ko)",
  explanatory_title: "호 표제 (관세청 호해설 첫 줄)",
  explanatory_body: "호해설 본문 (관세청 hsfdManl)",
  precedent: "관세청 분류사례 (precedent_cases)",
  trade_partner: "관세사 분류사례 (거래품명·규격·성분·표준품명)",
  translated_partner: "한↔영 변환 → 관세사 분류사례",
  translated_precedent: "한↔영 변환 → 관세청 분류사례",
  translated_master: "한↔영 변환 → DB 표준어휘",
  translated_explanatory: "한↔영 변환 → 호해설 본문",
  cas_precedent: "CAS 번호 → 관세청 분류사례 (화학물질)",
  cas_trade_partner: "CAS 번호 → 관세사 통관 spec·성분 (화학물질)",
  cas_pubchem: "CAS 번호 → PubChem 화학명 → HS 추정 (외부 보강)",
  relaxed: "핵심어 재검색 (조사 제거·축약)",
  typo: "오타 교정 재검색 (사전 낱말과 한 글자 차이)",
  semantic_only: "의미 매칭 (유사도순)",
};
function matchSourceLabel(src) {
  if (src === null || src === undefined) return MATCH_SOURCE_LABEL.null;
  return MATCH_SOURCE_LABEL[src] || `기타 (${src})`;
}

// 다중검색 행 추출 — 임의 아이템(컬럼명 케이스 무관) → {keyword, AIHSCode}.
// - preferKeyword=false(기본, hsClassifierQueue sessionStorage 소비부 기존 동작):
//   description + composition 을 항상 병합.
// - preferKeyword=true(크로스오리진 batch 핸드오프): item.keyword 가 이미
//   docuai2 프록시에서 desc+composition 병합 완료된 값이므로 그대로 사용 —
//   여기서 다시 병합하면 이중병합 버그가 됨. keyword 가 비어있을 때만
//   desc+composition fallback 병합(구조 불일치 대비 안전망).
function extractKeywordRow(it, { preferKeyword = false } = {}) {
  const norm = (k) =>
    String(k || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
  let desc = "";
  let comp = "";
  let ai = "";
  let kw = "";
  for (const k of Object.keys(it || {})) {
    const n = norm(k);
    if (n === "description" && !desc) desc = String(it[k] || "");
    else if (n === "composition" && !comp) comp = String(it[k] || "");
    else if (n === "aihscode" && !ai) ai = String(it[k] || "");
    else if (n === "keyword" && !kw) kw = String(it[k] || "");
  }
  if (preferKeyword && kw.trim()) {
    return { keyword: kw.trim(), AIHSCode: ai };
  }
  const keyword = comp ? `${desc.trim()} ${comp.trim()}`.trim() : desc.trim();
  return { keyword, AIHSCode: ai };
}

export default function LeftAiPanel({
  currentChapter,
  onAnyAction,
  onSearchContextChange,
  onGoogleImageOpen, // "구글" 체크 ON + 검색 시 부모에 keyword 전달 (가운데 panel overlay)
  onSearchResults, // 검색 완료 시 tariff 응답 끌어올림 — 가운데 종합 뷰가 재사용
  onSubdivisionPreview, // 검색어 단건 bulk의 국내세분 compact shadow — 중앙 비교 카드
}) {
  // mode = 'ai' | 'auto' | 'batch'. 관세율표(auto)가 기본이고 PROVE(ai)는 명시 선택.
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("auto");
  const [expand, setExpand] = useState(false); // 한글 합성어 자동 분해 (opt-in)
  const [semanticOn, setSemanticOn] = useState(false); // 의미 검색 (precedents-search hybrid mode)
  const [chapters, setChapters] = useState([]);
  // 매칭 세번 나열 (문의 "키워드 검색 결과 나열" — pineapple 등 검색 시 류 칩만
  // 나와 결과가 안 보인다는 피드백) — tariff-search items 를 목록으로 노출.
  const [results, setResults] = useState([]);
  const [resultsOpen, setResultsOpen] = useState(false); // 12건 초과 시 더보기
  // 호·소호 표제 매칭 (CLHS 관세율표 검색결과 패리티) — 잎에 없는 호 표제·
  // 소호(예: 2009.4 파인애플주스) 매칭. backend heading_matches.
  const [headingMatches, setHeadingMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [translation, setTranslation] = useState(null); // 한↔영 번역 hint
  const [matchSource, setMatchSource] = useState(null); // 어떤 DB 어휘 매칭으로 hit 했는지 (fallback type)
  const [refinedTokens, setRefinedTokens] = useState(null); // 완화 재검색 토큰 (backend refined_tokens)
  const [corrected, setCorrected] = useState(null); // 오타 교정 재검색 {query, original} (backend corrected_query, 43차)
  const [matchTokens, setMatchTokens] = useState([]); // 결과 row 노란 highlight 토큰 (backend match_tokens)
  const [splitTokens, setSplitTokens] = useState(null); // 확장 분해 결과 (예: ["가죽","가방"])
  const [lastQ, setLastQ] = useState(""); // 검색 컨텍스트 — 마지막 실제 검색어 (input q 와 별개)
  const [googleHintOn, setGoogleHintOn] = useState(false); // "구글" 체크 — 0건 시 Google 보강 (opt-in)
  const [googleHint, setGoogleHint] = useState(null); // { summary, sources, images, query }
  // 다중검색 — 그리드 (keyword + AI HSCode). 행 클릭 시 그 keyword 로 doSearch 트리거.
  const [batchRows, setBatchRows] = useState(() =>
    Array.from({ length: 3 }, () => ({ keyword: "", AIHSCode: "" })),
  );
  const [batchActiveRow, setBatchActiveRow] = useState(null); // 마지막 클릭한 행 index (시각 표시)
  const [batchClassifying, setBatchClassifying] = useState(false);
  const [batchStats, setBatchStats] = useState(null); // { total, success, distribution }
  const [batchGridHeight, setBatchGridHeight] = useState(280); // 그리드 세로 폭 (drag handle 로 조정)
  const batchResizeStartRef = useRef(null); // { y, height }
  const batchAbortRef = useRef(null); // batch-flash AbortController
  const abortRef = React.useRef(null); // 검색 fetch abort controller — 사용자 중지 버튼용
  const previewSeqRef = React.useRef(0); // 빠른 연속검색의 늦은 shadow 응답 차단

  const loadSubdivisionPreview = useCallback(
    async (queryStr, signal, productDetails, confirmedFacts) => {
      if (!onSubdivisionPreview) return;
      const seq = ++previewSeqRef.current;
      onSubdivisionPreview({ q: queryStr, loading: true, row: null, error: null });
      const token = localStorage.getItem("token");
      try {
        // 첫 계산은 결정례 힌트 없이(2026-09-29) — 긴 서술형은 힌트가 3~60초 걸려 카드가 늦게 떴다. 힌트는 바로 뒤에 따로 받아 합친다.
        const result = await fetchSubdivisionPreview({
          query: queryStr,
          productDetails,
          confirmedFacts,
          token,
          signal,
          withHints: false,
        });
        if (seq !== previewSeqRef.current || signal?.aborted) return;
        onSubdivisionPreview({
          q: queryStr,
          loading: false,
          row: result.row,
          error: result.error,
        });
        if (result.row && result.row.status !== "error") {
          const hints = await fetchPrecedentHints({ query: queryStr, token, signal, withModel: true, productDetails });
          if (hints && seq === previewSeqRef.current && !signal?.aborted) {
            onSubdivisionPreview({ q: queryStr, loading: false, row: { ...result.row, precedent_hints: hints }, error: result.error });
          }
        }
      } catch (error) {
        if (seq !== previewSeqRef.current) return;
        onSubdivisionPreview({
          q: queryStr,
          loading: false,
          row: null,
          error: error?.name === "AbortError" ? "cancelled" : String(error?.message || error),
        });
      }
    },
    [onSubdivisionPreview],
  );

  // 검색 컨텍스트 부모에게 알림 — 우측 EvidencePanel 이 검색어/매칭/모드 활용.
  // 검색 실행 후 (lastQ 갱신) + matchTokens / semanticOn / expand 변화 시.
  useEffect(() => {
    if (!onSearchContextChange) return;
    if (!lastQ) {
      onSearchContextChange(null);
      return;
    }
    onSearchContextChange({
      q: lastQ,
      matchTokens,
      semanticOn,
      expand,
    });
  }, [lastQ, matchTokens, semanticOn, expand, onSearchContextChange]);

  // 딥러닝 메뉴 (PageBasedGridPanel) 에서 sessionStorage 로 push 한 다중검색 queue
  // mount 시 (또는 다시 진입 시) 자동 load + batch 탭 활성화 + queue clear (30분 TTL).
  //
  // ※ 크로스오리진 핸드오프(?batch=<id>) 가 있으면 이 sessionStorage 경로는 스킵 —
  //   docuai2 등 외부 앱은 동일 오리진이 아니라 sessionStorage 를 공유할 수 없으므로
  //   loginhs 백엔드 GET /api/onto/classify/batch/{id} 로 대신 로드한다.
  useEffect(() => {
    const batchId = new URLSearchParams(window.location.search).get("batch");
    if (batchId) {
      const token = localStorage.getItem("token");
      fetch(`/api/onto/classify/batch/${encodeURIComponent(batchId)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const items = Array.isArray(d?.items) ? d.items : [];
          // items 는 docuai2 프록시가 이미 desc+composition 을 keyword 로 병합해 넘긴다 —
          // preferKeyword: true 로 여기서 재병합하지 않는다(이중병합 버그 방지).
          const rows = items
            .map((it) => extractKeywordRow(it, { preferKeyword: true }))
            .filter((r) => r.keyword);
          if (rows.length) {
            setBatchRows(rows);
            setMode("batch");
            if (onAnyAction) onAnyAction();
          }
        })
        .catch(() => {
          // 만료(404)/네트워크 오류 — 핸드오프는 opt-in 이므로 조용히 무시.
        })
        .finally(() => {
          // batch 파라미터 제거 — 새로고침 시 재조회/재소비 방지.
          const u = new URL(window.location.href);
          u.searchParams.delete("batch");
          window.history.replaceState({}, "", u.pathname + u.search + u.hash);
        });
      return; // sessionStorage 경로 스킵
    }
    const tryConsume = () => {
      try {
        const raw = sessionStorage.getItem("hsClassifierQueue");
        if (!raw) return;
        const parsed = JSON.parse(raw);
        const items = Array.isArray(parsed?.items) ? parsed.items : null;
        const ts = Number(parsed?.timestamp) || 0;
        if (!items || items.length === 0) {
          sessionStorage.removeItem("hsClassifierQueue");
          return;
        }
        if (ts && Date.now() - ts > 30 * 60 * 1000) {
          sessionStorage.removeItem("hsClassifierQueue");
          return;
        }
        const importedRows = items
          .map((it) => extractKeywordRow(it))
          .filter((r) => r.keyword);
        if (importedRows.length === 0) {
          sessionStorage.removeItem("hsClassifierQueue");
          return;
        }
        setBatchRows(importedRows);
        setMode("batch");
        sessionStorage.removeItem("hsClassifierQueue");
        if (onAnyAction) onAnyAction();
      } catch (e) {
        console.warn("[LeftAiPanel] queue consume fail:", e);
      }
    };
    tryConsume();
    // 같은 페이지에서 push 한 경우 (full mount 안 됨) — popstate listener 로 재확인
    window.addEventListener("popstate", tryConsume);
    return () => window.removeEventListener("popstate", tryConsume);
  }, [onAnyAction]);

  // 제안 카드가 조회 완료를 기다리도록 문맥에 표지를 세운다(AuditSearchContext.lookupInFlight).
  const { setLookupInFlight } = useAuditSearch();
  const markLookup = useCallback((value) => { if (setLookupInFlight) setLookupInFlight(value); }, [setLookupInFlight]);

  const doSearch = useCallback(
    async (queryOverride, opts = {}) => {
      const queryStr = (queryOverride ?? q).trim();
      if (!queryStr) return;
      const noNav = !!opts.noNavigate; // 분류코드 행 클릭 등: 직접 navigate 가 뒤따르므로 sq 스테이지 push 억제
      const effMode = opts.mode || mode; // 센터바 등 명시 모드 우선(setMode 갱신 레이스 회피)
      const proveMode = effMode === "ai" || effMode === "ontology";
      const lookupMode = proveMode ? "auto" : effMode;
      // CielHS 풍 검색어 type 자동 감지 — 숫자만 입력하면 hierarchical
      // navigate (사용자 manual chip 클릭 없이). 키워드는 기존 AI 검색 flow.
      const digits = queryStr.replace(/[^0-9]/g, "");
      // HS 코드 입력 감지(CielHS 풍) — 순수 숫자(2009) 또는 점 포함 코드(2009.4·08.04·
      //   0804.30-0000). 점이 있으면 HS 표기로 간주. ⚠CAS 번호(50-00-0 처럼 대시만·점 없음)는
      //   제외 → 키워드(CAS 검색)로 흐름. 문자 포함 키워드도 종합 뷰로.
      const isHsCode =
        (/^[0-9]+$/.test(queryStr) ||
          (queryStr.includes(".") && /^[0-9][0-9.\s-]*$/.test(queryStr))) &&
        (digits.length === 2 || digits.length >= 4);
      const resetAll = () => {
        previewSeqRef.current += 1;
        if (onSubdivisionPreview) onSubdivisionPreview(null);
        setLoading(false);
        setErr(null);
        setChapters([]);
        setResults([]);
        setResultsOpen(false);
        setHeadingMatches([]);
        setMatchSource(null);
        setCorrected(null);
        setMatchTokens([]);
        setRefinedTokens(null);
        setSplitTokens(null);
        setLastQ("");
        setQ(""); // type 감지 navigate 후 input clear (이전 "결과 없음" 메시지 trigger 방지)
      };
      if (isHsCode) {
        // 세번/호/소호 입력 → 해당 상세 화면으로 직행(CielHS 로직). 키워드만 종합 뷰.
        resetAll();
        if (onAnyAction) onAnyAction();
        if (digits.length === 2) {
          navigateAuto({ chapter: digits }); // 류
        } else if (digits.length >= 10) {
          navigateAuto({ hs: digits.slice(0, 4), hsk: digits.slice(0, 10) }); // 세번
        } else {
          // 호(4) → 호 전체 / 소호·부분(5~9) → 그 소호(prefix)만 필터해 표시(CielHS)
          navigateAuto({
            hs: digits.slice(0, 4),
            sub: digits.length >= 5 ? digits : undefined,
          });
        }
        return;
      }
      // 키워드 검색 — 우측 panel 자동 펼침 (5-section 검색 결과 view)
      if (onAnyAction) onAnyAction();
      // 이전 검색 abort + 새 controller (사용자 중지 버튼)
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      const myController = abortRef.current;
      setLoading(true);
      markLookup(queryStr);
      setErr(null);
      setGoogleHint(null); // 새 검색 → 옛 Google 보강 즉시 clear
      setChapters([]);
      setResults([]);
      setResultsOpen(false);
      setHeadingMatches([]);
      setTranslation(null);
      setMatchSource(null);
      setMatchTokens([]);
      setRefinedTokens(null);
      setSplitTokens(null);
      setLastQ(queryStr); // 검색 컨텍스트 부모 알림 (우측 EvidencePanel 가 받음)
      // 메인 검색은 공식 관세율표 lookup 을 그대로 유지하고, 국내세분 결정표는
      // 같은 검색어의 단건 bulk shadow를 병렬로 계산해 중앙 비교 카드에만 싣는다.
      // 응답은 기존 세번을 절대 덮어쓰지 않는다.
      // 카드·PROVE에서 이미 확인한 결정트리 사실은 같은 검색어의 사전 점검에 그대로 싣는다.
      void loadSubdivisionPreview(
        queryStr, abortRef.current.signal, opts.productDetails,
        opts.confirmedFacts || loadTreeFacts(queryStr),
      );
      // 번역 hint — Google Translate proxy (auto detect). 비동기.
      fetch(`/api/onto/translate?q=${encodeURIComponent(queryStr)}&target=auto`)
        .then((rr) => (rr.ok ? rr.json() : null))
        .then((td) => {
          if (
            td &&
            td.translated &&
            td.translated.toLowerCase() !== queryStr.toLowerCase()
          ) {
            setTranslation({
              source: td.source_lang,
              target: td.target,
              text: td.translated,
            });
          }
        })
        .catch(() => {});
      // B 분류사례 통합 view 는 우측 EvidencePanel 의 PrecedentsExcerpt 가
      // 이미 같은 hs4 의 사례를 자동 표시하므로 중복 제거 (좌측 panel 제거).
      try {
        // mode "auto" = keyword 우선 → 0 시 hybrid → 0 시 translate 보강 후 재시도.
        // 사용자 구어 "핸드폰" → DB 정식 어휘 "cell phone / 전화기 / 셀룰러" 매핑.
        const fetchTariff = async (backendMode, queryParam) => {
          const u = new URL("/api/onto/tariff-search", window.location.origin);
          u.searchParams.set("q", queryParam || queryStr);
          u.searchParams.set("mode", backendMode);
          u.searchParams.set("limit", "30");
          if (expand) u.searchParams.set("expand", "true");
          if (opts.noCorrect) u.searchParams.set("correct", "false"); // 처음 검색어 그대로 보기(오타 교정 없이)
          const r = await fetch(u.toString(), {
            signal: abortRef.current?.signal,
            headers: userAuthHeaders(),
          });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        };
        let d;
        if (lookupMode === "auto") {
          // 관세율표 직접 일치와 호 표제가 모두 없을 때만 품질 게이트가 있는 의미검색을 시도한다.
          d = await fetchTariff("keyword");
          const hasDirectTariff = Array.isArray(d.items) && d.items.length > 0;
          const hasHeadingMatch =
            Array.isArray(d.heading_matches) && d.heading_matches.length > 0;
          if (!hasDirectTariff && !hasHeadingMatch) {
            d = await fetchTariff("hybrid");
          }
        } else {
          d = await fetchTariff(lookupMode);
        }

        // 분류사례 + 관세사 분류사례 fetch 는 우측 EvidencePanel 이 담당 — 좌측 LeftAiPanel
        // 은 chapter chip 만. searchContext (q, matchTokens, semanticOn, expand) 가
        // 부모 → EvidencePanel 으로 props 전달됨 (useEffect 자동 알림).
        // 아래 코드와의 호환 — d 가 그대로 사용됨 (별도 변수)
        if (!d) throw new Error("no data");
        // 더 이상 r/json() 호출 안 함 (위에서 d 받음)
        // distinct chapter 추출 + count + sample row (chapter 별 첫 item 의 popum).
        // 사용자가 매칭 chapter 의 의미 인지 가능 — "44 목재 (TIMBER, ROUGH 등)"
        const chMap = new Map();
        for (const it of d.items || []) {
          const ch = it.chapter;
          if (!ch) continue;
          if (!chMap.has(ch))
            chMap.set(ch, {
              count: 0,
              sample_ko: it.desc_ko || "",
              sample_en: it.desc || "",
            });
          chMap.get(ch).count++;
        }
        let list = Array.from(chMap.entries())
          .map(([ch, v]) => ({
            ch,
            count: v.count,
            sample_ko: v.sample_ko,
            sample_en: v.sample_en,
          }))
          .sort((a, b) => b.count - a.count);
        // chapter ranking — top1 압도적 (≥ 5 매칭) 이면 1건짜리 minor chapter
        // 제거 (cross-noise). 사용자에게 정답 chapter 만 명확히 노출.
        if (list.length > 1 && list[0].count >= 5) {
          const topCount = list[0].count;
          const filtered = list.filter(
            (it) => it.count > 1 || it.count * 5 >= topCount,
          );
          if (filtered.length >= 1) list = filtered;
        }
        setChapters(list);
        setResults(d.items || []); // 매칭 세번 나열 (문의 "키워드 검색 결과 나열")
        setHeadingMatches(d.heading_matches || []); // 호·소호 표제 매칭 (keyword 모드만 제공)
        // "구글" 체크 ON → Google 보강 (opt-in, 외부 전송) — 결과 quality 와 무관하게 호출
        if (googleHintOn) {
          // Google 이미지 검색 새 창 — iframe embed 가 X-Frame-Options 차단되므로 직접 새 창 열기
          const gUrl = `https://www.google.co.kr/search?q=${encodeURIComponent(queryStr)}&udm=2`;
          window.open(gUrl, "_blank", "noopener,noreferrer");
          fetch("/api/onto/visual-hint", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ q: queryStr }),
            signal: abortRef.current?.signal,
          })
            .then((rr) => (rr.ok ? rr.json() : null))
            .then((vh) => {
              if (vh) setGoogleHint(vh);
            })
            .catch(() => {});
        } else {
          setGoogleHint(null);
        }
        // 매칭 source — backend fallback type 표시 (사용자 어휘 ↔ DB 어휘 추적)
        setMatchSource(d.fallback || null);
        // 오타 교정 재검색(43차, 구글식 "다음에 대한 결과 표시 중") — 교정 검색어와 처음 검색어를 함께 든다
        setCorrected(
          d.fallback === "typo" && d.corrected_query
            ? { query: d.corrected_query, original: queryStr }
            : null,
        );
        // 완화 재검색 토큰 (relaxed fallback 시 backend 가 실제 재검색한 핵심어 노출)
        setRefinedTokens(
          Array.isArray(d.refined_tokens) && d.refined_tokens.length
            ? d.refined_tokens
            : null,
        );
        // 매칭 token — 결과 row 노란 highlight (Step 5 hit 시 영문 token 도 포함)
        setMatchTokens(Array.isArray(d.match_tokens) ? d.match_tokens : []);
        // 확장 분해 결과 — backend 가 expand=true 로 분해한 토큰. UI hint 노출.
        if (d.expand && Array.isArray(d.tokens) && d.tokens.length >= 2) {
          setSplitTokens(d.tokens);
        } else {
          setSplitTokens(null);
        }
        // translate fallback hit 했고 사전 translation hint 아직 비어있다면 보강
        if (d.translated_query && !translation) {
          setTranslation({
            source: "ko",
            target: "en",
            text: d.translated_query,
          });
        }

        // 검색 완료 → 가운데 '검색결과 종합' 스테이지 (CLHS 5-섹션 패리티).
        // 첫 호 자동 진입 제거 — 항상 종합 뷰 먼저, 행 클릭으로 호/세번 상세 진입.
        if (onSearchResults) {
          onSearchResults({
            q: queryStr,
            items: d.items || [],
            headingMatches: d.heading_matches || [],
            matchTokens: Array.isArray(d.match_tokens) ? d.match_tokens : [],
            fallback: d.fallback || null,
            // 서버 명칭 일치(동의어 경유, 34차). 셸 조회 봉투가 이것을 빠뜨려 카드가 키보드 → 8471.60-1020 파란 길을 못 그렸다(2026-09-15).
            nameMatches: Array.isArray(d.name_matches) ? d.name_matches : [],
            mode: d.mode || "keyword",
            productDetails: opts.productDetails || null,
            meaningExpansion: opts.meaningExpansion || null,
            ts: Date.now(), // 명시적 재검색 식별 — 같은 q 재제출 시 PROVE 재실행 트리거
          });
        }
        if (proveMode) {
          try { sessionStorage.setItem("loginhs_ai_run", queryStr); } catch { /* ignore */ }
        }
        if (!noNav) navigateSearchStage(queryStr, opts.tab || (proveMode ? "ai" : undefined));

        // PROVE 탭은 중앙 작업공간이 최신 결정트리로 판정하고, lookup은 후보 자료만 제공한다.
      } catch (e) {
        // 사용자가 "중지" 클릭한 경우 — 오류 표시 X
        if (e?.name !== "AbortError") setErr(String(e.message || e));
      } finally {
        setLoading(false);
        // 더 새 검색이 이미 시작됐으면(이 검색은 abort 됨) 그쪽 표지를 지우지 않는다.
        if (abortRef.current === myController) markLookup(null);
      }
    },
    [q, mode, expand, onSearchResults, onSubdivisionPreview, loadSubdivisionPreview, markLookup],
  );

  // 사용자 중지 버튼 — 현재 진행 중인 검색 fetch abort
  const cancelSearch = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    previewSeqRef.current += 1;
    setLoading(false);
    markLookup(null);
  }, [markLookup]);

  // 가운데 CenterSearchBar 제출 → 좌측 doSearch 실행(동일 동작 — 리스트·종합뷰·근거 함께 갱신).
  //   pendingSearch.mode(PROVE=ai / 관세율표=auto)로 탭 전환 + 해당 모드로 검색.
  const { pendingSearch, setPendingSearch } = useAuditSearch();
  useEffect(() => {
    if (!pendingSearch || !pendingSearch.q) return;
    const m = pendingSearch.mode || "auto";
    setQ(pendingSearch.q);
    if (m === "ai" || m === "ontology") {
      // 구 ontology 모드 요청도 PROVE로 승계한다. 관세율표 lookup은 병렬 자료로 유지한다.
      try {
        sessionStorage.setItem("loginhs_ai_run", pendingSearch.q);
      } catch {
        /* ignore */
      }
      setMode("ai");
      doSearch(pendingSearch.q, { mode: "ai", tab: "ai", productDetails: pendingSearch.productDetails, meaningExpansion: pendingSearch.meaningExpansion, confirmedFacts: pendingSearch.confirmedFacts });
    } else {
      setMode(m);
      doSearch(pendingSearch.q, { mode: m, productDetails: pendingSearch.productDetails, meaningExpansion: pendingSearch.meaningExpansion, confirmedFacts: pendingSearch.confirmedFacts });
    }
    setPendingSearch(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSearch]);

  // 딥링크(?audit=onto-detail&sq=…)로 바로 들어오면 검색을 한 번 실행해 왼쪽 목록·사전 점검 카드가
  // 채워지게 한다. 센터바 제출(pendingSearch)이 있으면 그쪽이 맡고, 컴포넌트 마운트당 한 번만 돈다.
  const deepLinkRanRef = useRef(false);
  useEffect(() => {
    if (deepLinkRanRef.current || pendingSearch) return;
    let params;
    try { params = new URLSearchParams(window.location.search); } catch { return; }
    const sq = (params.get("sq") || "").trim();
    if (!sq || params.get("audit") !== "onto-detail") return;
    if (params.get("batch")) return;
    deepLinkRanRef.current = true;
    setQ(sq);
    doSearch(sq, { noNavigate: true, confirmedFacts: loadTreeFacts(sq) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToChapter = (ch) => navigateAuto({ chapter: ch });

  // ─── 다중검색 그리드 핸들러 ───────────────────────────────────────
  // 행 선택 → 그 행의 keyword 를 검색창 q 에 set + doSearch 자동 실행.
  // 기존 검색 흐름 그대로 트리거 (관세율표 후보 + 우측 EvidencePanel 동시 update).
  const batchColumns = React.useMemo(
    () => [
      {
        dataField: "keyword",
        headerText: "Keyword",
        width: "100%", // 가변 — 패널 너비를 채우고, 패널 드래그로 넓히면 같이 확장(긴 값 표시)
        minWidth: 160,
        editable: true,
      },
      {
        dataField: "AIHSCode",
        headerText: "AI HS",
        width: 132,
        editable: true,
        style: "left-align",
        // 보정 표식 — 미존재 세번을 유효 세번으로 snap 한 경우 "보정", 호 미존재면 "미확인".
        labelFunction: (rowIndex, columnIndex, value, headerText, item) => {
          if (!value) return value || "";
          if (item && item._snapped) return `${value} ↩보정`;
          if (item && item._hskInvalid) return `${value} ⚠미확인`;
          return value;
        },
        styleFunction: (rowIndex, columnIndex, value, headerText, item) => {
          if (item && item._snapped) return "hsk-snapped-cell";
          if (item && item._hskInvalid) return "hsk-invalid-cell";
          return null;
        },
      },
    ],
    [],
  );

  const handleBatchGridChange = useCallback((newRows) => {
    setBatchRows(newRows);
  }, []);

  const handleBatchCellClick = useCallback(
    (event) => {
      const rowIdx = event?.rowIndex;
      if (rowIdx == null || rowIdx < 0) return;
      // ⚠️ editable 셀(keyword 컬럼)은 cellClick 대신 cellSelected 가 발화하는데
      //    cellSelected 이벤트엔 event.item 이 없을 수 있다 → batchRows[rowIdx] 로 폴백.
      //    (이 누락이 "하단/우측이 안 바뀌는" 원인이었음)
      const row = event?.item || batchRows[rowIdx];
      if (!row) return;
      setBatchActiveRow(rowIdx);
      const kw = String(row.keyword || "").trim();
      if (kw) setQ(kw);
      // ✅ 분류된 세번이 있으면 → 그 세번 상세로 navigate.
      //    중앙(호 관세율표)과 우측(관세·FTA·근거)이 해당 세번으로 전환되어
      //    "이게 정말 그 품목인지" 바로 검토 가능 (예: 6404110000 → 6404호 + 세번 highlight).
      const code = String(row.AIHSCode || "").replace(/\D/g, "");
      if (code.length >= 4) {
        // 하단(번역·매칭 결과)는 그 keyword 로 갱신하되 스테이지 navigate 는 억제
        // (noNavigate) — navigateAuto 가 "분류된 세번" 상세로 직행하므로, doSearch
        // 완료 시 sq 종합 뷰로 되튕기는 레이스를 막는다 (중앙 호표 + 우측 관세·근거 전환).
        if (kw) doSearch(kw, { noNavigate: true });
        navigateAuto({
          hs: code.slice(0, 4),
          hsk: code.length === 10 ? code : undefined,
        });
        return;
      }
      // 아직 미분류(세번 없음) 행 → 기존 keyword 검색 (검색 결과 + 우측 evidence).
      if (kw) doSearch(kw);
    },
    [doSearch, batchRows],
  );

  const addBatchRow = useCallback(() => {
    setBatchRows((prev) => [...prev, { keyword: "", AIHSCode: "" }]);
  }, []);
  const clearEmptyBatchRows = useCallback(() => {
    setBatchRows((prev) =>
      prev.filter((r) => (r.keyword || "").trim() || (r.AIHSCode || "").trim()),
    );
  }, []);

  // 그리드 세로 폭 drag resize — mousedown → window mousemove/mouseup 리스너 설치
  const handleBatchResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      batchResizeStartRef.current = {
        y: e.clientY,
        height: batchGridHeight,
      };
      const onMove = (ev) => {
        const start = batchResizeStartRef.current;
        if (!start) return;
        const delta = ev.clientY - start.y;
        const next = Math.max(140, Math.min(700, start.height + delta));
        setBatchGridHeight(next);
      };
      const onUp = () => {
        batchResizeStartRef.current = null;
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [batchGridHeight],
  );

  // batch-flash 일괄 분류 — 모든 행의 keyword → Gemini 2.5 Flash 1-call (1-3s)
  const handleBatchClassifyAll = useCallback(async () => {
    if (batchClassifying) return;
    const validRows = batchRows
      .map((r, i) => ({ rowIndex: i, keyword: (r.keyword || "").trim() }))
      .filter((r) => r.keyword);
    if (validRows.length === 0) {
      alert("분류할 keyword 가 없습니다. 행에 keyword 를 입력하세요.");
      return;
    }
    if (validRows.length > 50) {
      alert("한번에 최대 50건. 분할 권장.");
      return;
    }
    setBatchClassifying(true);
    setBatchStats(null);
    // 이전 진행 중인 분류 abort
    if (batchAbortRef.current) batchAbortRef.current.abort();
    const controller = new AbortController();
    batchAbortRef.current = controller;
    try {
      const items = validRows.map((r, i) => ({
        index: i,
        description: r.keyword,
        composition: "",
      }));
      const res = await fetch("/api/hs-classify/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBatchRows((prev) => {
        const next = [...prev];
        (data.results || []).forEach((r) => {
          const v = validRows[r.index];
          if (!v || !r.ok || !r.hsk_10digit) return;
          next[v.rowIndex] = {
            ...next[v.rowIndex],
            AIHSCode: r.hsk_10digit,
            // 권위 관세율표 대조 표식 — 환각 세번 → 유효 세번 보정 / 미확인.
            _snapped: !!r.hsk_snapped,
            _hskOriginal: r.hsk_original || null,
            _hskInvalid: r.hsk_valid === false,
          };
        });
        return next;
      });
      setBatchStats({
        total: data.total,
        success: data.success,
        snapped: data.snapped || 0,
        invalid: data.invalid || 0,
        distribution: data.confidence_distribution,
        elapsed_ms: data.response_time_ms,
      });
      // 분류 후 첫 분류 행 keyword 로 자동 검색 — 단건 검색 결과를 하단에 즉시 노출
      const firstOk = (data.results || []).find(
        (r) => r && r.ok && r.hsk_10digit,
      );
      if (firstOk) {
        const v = validRows[firstOk.index];
        if (v) {
          setBatchActiveRow(v.rowIndex);
          setQ(v.keyword);
          doSearch(v.keyword);
        }
      }
    } catch (e) {
      if (e.name === "AbortError") {
        console.log("[batch-flash] cancelled by user");
      } else {
        console.error("[batch-flash]", e);
        alert(`다중분류 실패: ${e.message}`);
      }
    } finally {
      batchAbortRef.current = null;
      setBatchClassifying(false);
    }
  }, [batchClassifying, batchRows, doSearch]);

  const cancelBatchClassify = useCallback(() => {
    if (batchAbortRef.current) {
      batchAbortRef.current.abort();
      batchAbortRef.current = null;
    }
    setBatchClassifying(false);
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: 12,
        background: "var(--audit-bg-elevated)",
        border: "1px solid var(--audit-border)",
        borderRadius: 6,
      }}
    >
      {/* PROVE / 관세율표 / 다중검색 — PROVE가 기본 분류 진입점. */}
      <SearchModeToggle mode={mode} setMode={setMode} modes={isVerdictTabVisible() ? MODES : MODES.filter((m) => m.id !== "ai")} />

      {/* 공통 검색창 — PROVE / 관세율표 탭에서 표시. 다중검색은 그리드가 keyword source. */}
      {mode !== "batch" && (
        <div style={{ display: "flex", gap: 4 }}>
          <input
            value={q}
            onChange={(e) => setQ(formatHsInput(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter") doSearch();
            }}
            onPaste={(e) => {
              // Korean IME 또는 React controlled state 의 paste sync 이슈 대비
              // — clipboardData 직접 read + cursor 위치 보존
              const text = e.clipboardData?.getData("text");
              if (text == null) return;
              e.preventDefault();
              const input = e.currentTarget;
              const start = input.selectionStart ?? q.length;
              const end = input.selectionEnd ?? q.length;
              const newQ = q.slice(0, start) + text + q.slice(end);
              setQ(newQ);
              // cursor 위치 갱신 — append 후 cursor 위치 유지
              setTimeout(() => {
                if (input && input.setSelectionRange) {
                  const pos = start + text.length;
                  input.setSelectionRange(pos, pos);
                }
              }, 0);
            }}
            placeholder="예: handbag, 화장품, …"
            style={{
              flex: 1,
              padding: "5px 8px",
              border: "1px solid var(--audit-border)",
              borderRadius: 4,
              background: "var(--audit-bg)",
              color: "var(--audit-text)",
              fontFamily: "inherit",
              fontSize: 12,
              minWidth: 0,
            }}
          />
          <button
            onClick={() => (loading ? cancelSearch() : doSearch())}
            disabled={!loading && !q.trim()}
            title={loading ? "검색 중지" : "검색"}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              padding: "5px 8px",
              border: loading
                ? "1px solid #d93025"
                : "1px solid var(--audit-accent)",
              background: loading
                ? "#fff"
                : q.trim()
                  ? "var(--audit-accent)"
                  : "var(--audit-bg-subtle)",
              color: loading
                ? "#d93025"
                : q.trim()
                  ? "white"
                  : "var(--audit-text-muted)",
              borderRadius: 4,
              cursor: loading || q.trim() ? "pointer" : "not-allowed",
              fontFamily: "inherit",
              fontSize: 12,
            }}
          >
            {loading ? (
              <>✕ 중지</>
            ) : (
              <>
                <Search size={12} /> 검색
              </>
            )}
          </button>
        </div>
      )}

      {/* 검색 옵션 토글 row — PROVE·관세율표·다중검색이 같은 lookup 자료를 사용한다. */}
      {(mode === "ai" || mode === "auto" || mode === "batch") && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <SearchOptToggle
            label="분해"
            checked={expand}
            onChange={setExpand}
            activeBorder="var(--audit-text)"
            title="한글 4글자 합성어를 2+2 로 자동 분해 (예: 가죽가방 → 가죽 + 가방)"
            hint={expand && q.trim() ? `· ${q.trim()} → 분해` : ""}
          />
          <SearchOptToggle
            label="의미"
            checked={semanticOn}
            onChange={setSemanticOn}
            activeBorder="var(--audit-text)"
            title="분류사례를 임베딩 의미 매칭 추가 (Gemini embedding + RRF). 키워드 매칭과 같이 노출."
          />
          <SearchOptToggle
            label="구글"
            checked={googleHintOn}
            onChange={setGoogleHintOn}
            activeBorder="var(--audit-text)"
            title="검색 결과 0건 시 Google 검색 (Gemini grounding) 으로 키워드 보강 + 출처 표시. 외부 전송 동의 필요."
          />
        </div>
      )}

      {/* ── "다중검색" 탭 — 그리드 (keyword / AI HSCode) + 행 클릭 → doSearch 트리거 ── */}
      {mode === "batch" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* 액션 row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={addBatchRow}
              style={{
                padding: "4px 10px",
                fontSize: 12,
                border: "1px solid var(--audit-border)",
                background: "var(--audit-bg)",
                color: "var(--audit-text)",
                borderRadius: 4,
                cursor: "pointer",
              }}
              title="빈 행 1개 추가"
            >
              + 행
            </button>
            <button
              onClick={clearEmptyBatchRows}
              style={{
                padding: "4px 10px",
                fontSize: 12,
                border: "1px solid var(--audit-border)",
                background: "var(--audit-bg)",
                color: "var(--audit-text-muted)",
                borderRadius: 4,
                cursor: "pointer",
              }}
              title="keyword/AI 모두 빈 행만 삭제"
            >
              빈 행 삭제
            </button>
            <button
              onClick={
                batchClassifying ? cancelBatchClassify : handleBatchClassifyAll
              }
              style={{
                padding: "4px 12px",
                fontSize: 12,
                border: batchClassifying
                  ? "1px solid #d93025"
                  : "1px solid var(--audit-accent)",
                background: batchClassifying ? "#fff" : "var(--audit-accent)",
                color: batchClassifying ? "#d93025" : "white",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 500,
              }}
              title={
                batchClassifying
                  ? "진행 중 — 클릭하면 취소"
                  : "Gemini 2.5 Flash 1-call 일괄 분류 (1-3s)"
              }
            >
              {batchClassifying ? "✕ 취소" : "모두 분류"}
            </button>
            {batchStats && (
              <span
                style={{
                  fontSize: 12,
                  color: "var(--audit-text-muted)",
                  marginLeft: "auto",
                }}
                title={`${batchStats.elapsed_ms ? (batchStats.elapsed_ms / 1000).toFixed(1) + "s, " : ""}H ${batchStats.distribution?.high || 0} / M ${batchStats.distribution?.medium || 0} / L ${batchStats.distribution?.low || 0}${batchStats.snapped ? ` · 보정 ${batchStats.snapped}` : ""}${batchStats.invalid ? ` · 미확인 ${batchStats.invalid}` : ""}`}
              >
                {batchStats.success}/{batchStats.total}
                {batchStats.snapped > 0 && (
                  <span style={{ color: "#AD6800", marginLeft: 4 }}>
                    ↩{batchStats.snapped}
                  </span>
                )}
                {batchStats.invalid > 0 && (
                  <span style={{ color: "#CF1322", marginLeft: 4 }}>
                    ⚠{batchStats.invalid}
                  </span>
                )}
              </span>
            )}
          </div>

          {/* 안내 */}
          <div
            style={{
              fontSize: 12,
              color: "var(--audit-text-faint)",
              lineHeight: 1.5,
            }}
          >
            행 클릭 → 분류된 세번이면 그 세번 상세로 이동 (중앙·우측 전환) /
            미분류면 keyword 검색
          </div>

          {/* 그리드 + 하단 drag handle (세로 폭 조정) */}
          <div
            style={{
              height: batchGridHeight,
              border: "1px solid var(--audit-border)",
              borderRadius: 4,
              overflow: "hidden",
              background: "var(--audit-bg)",
            }}
          >
            <RichInvoiceGrid
              gridId="left-ai-batch-grid"
              columns={batchColumns}
              data={batchRows}
              onChange={handleBatchGridChange}
              onCellClick={handleBatchCellClick}
              footerSum={false}
              height="fill"
              showRowNumColumn={true}
              rowNumHeaderText="#"
              rowNumWidth={22}
              showStateColumn={false}
              autoFit={false}
              enableMovingColumn={false}
            />
          </div>
          <div
            onMouseDown={handleBatchResizeStart}
            title="드래그하여 그리드 세로 폭 조정"
            style={{
              height: 3,
              margin: "-6px 0 -6px",
              cursor: "ns-resize",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.3,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.9)}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.3)}
          >
            <div
              style={{
                width: 22,
                height: 2,
                background: "var(--audit-text-muted)",
                borderRadius: 2,
              }}
            />
          </div>

          {/* 마지막 검색 행 — 사용자가 keyword 수정 후 단건 재검색 가능 */}
          {batchActiveRow != null && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginTop: 0,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: "var(--audit-text-muted)",
                  flexShrink: 0,
                }}
              >
                row {batchActiveRow + 1}
              </span>
              <input
                value={q}
                onChange={(e) => setQ(formatHsInput(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") doSearch();
                }}
                placeholder="keyword 수정 후 검색"
                style={{
                  flex: 1,
                  padding: "4px 8px",
                  border: "1px solid var(--audit-border)",
                  borderRadius: 4,
                  background: "var(--audit-bg)",
                  color: "var(--audit-text)",
                  fontFamily: "inherit",
                  fontSize: 12,
                  minWidth: 0,
                }}
              />
              <button
                onClick={() => (loading ? cancelSearch() : doSearch())}
                disabled={!loading && !q.trim()}
                title={loading ? "검색 중지" : "이 keyword 로 재검색"}
                style={{
                  padding: "4px 10px",
                  fontSize: 12,
                  border: loading
                    ? "1px solid #d93025"
                    : "1px solid var(--audit-accent)",
                  background: loading
                    ? "#fff"
                    : q.trim()
                      ? "var(--audit-accent)"
                      : "var(--audit-bg-subtle)",
                  color: loading
                    ? "#d93025"
                    : q.trim()
                      ? "white"
                      : "var(--audit-text-muted)",
                  borderRadius: 4,
                  cursor: loading || q.trim() ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  flexShrink: 0,
                }}
              >
                {loading ? (
                  <>✕ 중지</>
                ) : (
                  <>
                    <Search size={11} /> 검색
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── 검색 결과 — PROVE의 병렬 후보자료와 관세율표 lookup ── */}
      {(mode === "ai" || mode === "auto" || mode === "batch") && (
        <>
          {loading && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                fontSize: 12,
                color: "var(--audit-text)",
                background: "var(--audit-bg-subtle)",
                border: "1px solid var(--audit-accent)",
                borderRadius: 4,
              }}
            >
              <span className="audit-spinner" />
              <span>검색 중…</span>
            </div>
          )}
          {err && (
            <div style={{ fontSize: 12, color: "#d93025" }}>오류: {err}</div>
          )}

          {/* 번역 hint — query 한↔영 자동 번역 (Google Translate proxy) */}
          {translation && translation.text && (
            <div
              key={`tr-${lastQ}`}
              className="audit-fade-in"
              style={{
                padding: "5px 8px",
                fontSize: 12,
                color: "var(--audit-text-muted)",
                background: "var(--audit-bg-subtle)",
                border: "1px solid var(--audit-border)",
                borderRadius: 4,
                lineHeight: 1.4,
              }}
              title={`${translation.source} → ${translation.target} (Google Translate)`}
            >
              <span style={{ color: "var(--audit-text-faint)" }}></span>
              <span style={{ color: "var(--audit-text)" }}>
                {translation.text}
              </span>
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 12,
                  color: "var(--audit-text-faint)",
                  fontFamily: "JetBrains Mono, monospace",
                }}
              >
                ({translation.source}→{translation.target})
              </span>
            </div>
          )}

          {/* 확장 분해 hint — 사용자 입력을 자동 분해한 토큰 시각화 (무채색 절제) */}
          {splitTokens && q.trim() && (
            <div
              style={{
                padding: "5px 8px",
                fontSize: 12,
                color: "var(--audit-text)",
                background: "var(--audit-bg)",
                border: "1px solid var(--audit-border)",
                borderRadius: 4,
                lineHeight: 1.4,
              }}
              title="한글 합성어 자동 분해 결과 — '확장' 토글 OFF 시 비활성"
            >
              <span style={{ color: "var(--audit-text-faint)" }}></span>
              <span
                style={{
                  fontFamily: "JetBrains Mono, monospace",
                  color: "var(--audit-text-muted)",
                }}
              >
                {q.trim()} →{" "}
              </span>
              {splitTokens.map((t, i) => (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <span style={{ color: "var(--audit-text-faint)" }}>
                      {" "}
                      ·{" "}
                    </span>
                  )}
                  <mark
                    style={{
                      background: "#fff3a0",
                      color: "inherit",
                      padding: "0 3px",
                      borderRadius: 2,
                      fontFamily: "JetBrains Mono, monospace",
                    }}
                  >
                    {t}
                  </mark>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* 매칭 류 결과 없음 안내 — 검색어 변경 가이드 (Google 풍 문법 + 의미 모드 권장) */}
          {!loading && !err && chapters.length === 0 && q.trim() && (
            <div
              key={`nomatch-${lastQ}`}
              className="audit-fade-in"
              style={{
                padding: 10,
                fontSize: 12,
                color: "var(--audit-text)",
                background: "var(--audit-bg-subtle)",
                border: "1px dashed var(--audit-border)",
                borderRadius: 4,
                lineHeight: 1.6,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                매칭 결과 0건
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--audit-text-muted)",
                  marginBottom: 4,
                }}
              >
                검색어 변경 시도:
              </div>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 16,
                  fontSize: 12,
                  color: "var(--audit-text-muted)",
                }}
              >
                <li>
                  공백으로 분리 — <code>가죽 지갑</code>{" "}
                  <span style={{ color: "var(--audit-text-faint)" }}>
                    (AND, 두 단어 모두 포함)
                  </span>
                </li>
                <li>
                  구체어 시도 — <code>핸드백</code>, <code>여행가방</code>
                </li>
                <li>
                  정확 구문 — <code>"가죽 지갑"</code>
                </li>
                <li>
                  제외 — <code>가죽 -신발</code>{" "}
                  <span style={{ color: "var(--audit-text-faint)" }}>
                    (NOT)
                  </span>
                </li>
                <li>
                  의미 검색 — 상단 모드를 <b>혼합</b> 또는 <b>의미</b> 로 전환
                </li>
              </ul>
            </div>
          )}

          {/* "구글" 체크 ON → Google 보강 (Gemini grounding) — 매칭 류 아래에 표시 (사용자 우선순위) */}
          {false /* moved below */ &&
            !loading &&
            !err &&
            googleHintOn &&
            googleHint && (
              <div
                style={{
                  padding: 10,
                  fontSize: 12,
                  color: "var(--audit-text)",
                  background: "#dbeafe",
                  borderRadius: 4,
                  lineHeight: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontWeight: 600,
                    color: "#1a73e8",
                  }}
                >
                  Google 검색 보강
                  <span
                    style={{
                      fontSize: 12,
                      color: "var(--audit-text-muted)",
                      fontWeight: 400,
                    }}
                  >
                    ({googleHint.query})
                  </span>
                </div>
                {googleHint.summary && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--audit-text)",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.6,
                    }}
                  >
                    {googleHint.summary}
                  </div>
                )}
                {googleHint.sources?.length > 0 && (
                  <div
                    style={{ fontSize: 12, color: "var(--audit-text-muted)" }}
                  >
                    출처:{" "}
                    {googleHint.sources.slice(0, 3).map((s, i) => (
                      <span key={i}>
                        {i > 0 && " · "}
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#1a73e8", textDecoration: "none" }}
                        >
                          {s.title}
                        </a>
                      </span>
                    ))}
                  </div>
                )}
                {googleHint.images?.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: 4,
                      overflowX: "auto",
                      paddingTop: 4,
                    }}
                  >
                    {googleHint.images.slice(0, 6).map((img, i) => (
                      <a
                        key={i}
                        href={img.context_url || img.image_url}
                        target="_blank"
                        rel="noreferrer"
                        title={img.title}
                      >
                        <img
                          src={img.thumbnail_url}
                          alt={img.title}
                          style={{
                            width: 60,
                            height: 60,
                            objectFit: "cover",
                            borderRadius: 3,
                            border: "1px solid var(--audit-border)",
                          }}
                        />
                      </a>
                    ))}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--audit-text-faint)",
                    marginTop: 2,
                  }}
                >
                  ⚠ HS 분류 결과 아님 — 외부 참고용. Google 에 검색어 전송됨.
                </div>
              </div>
            )}

          {/* 매칭 류 chip list. 분산 (4+ chapter) 일 때는 노이즈 안내 + 접기 default. */}
          {/* 오타 교정 안내(43차) — 교정 검색어로 찾았음을 먼저 알리고, 처음 검색어 결과로 되돌아갈 길을 둔다(교정은 제안) */}
          {!loading && corrected && (
            <div
              key={`typo-${lastQ}`}
              data-testid="typo-corrected-notice"
              className="audit-fade-in"
              style={{
                padding: "8px 10px",
                marginBottom: 8,
                fontSize: 12.5,
                lineHeight: 1.6,
                border: "1px solid var(--audit-border)",
                borderRadius: 4,
                background: "var(--audit-bg-subtle)",
                color: "var(--audit-text)",
              }}
            >
              <div>
                다음 검색어로 찾았습니다:{" "}
                <b style={{ color: "#2563eb" }}>{corrected.query}</b>
              </div>
              <div style={{ color: "var(--audit-text-muted)" }}>
                처음 검색어{" "}
                <span style={{ fontWeight: 600, color: "var(--audit-text)" }}>
                  {corrected.original}
                </span>
                {" · "}
                <button
                  type="button"
                  onClick={() => doSearch(corrected.original, { noCorrect: true })}
                  style={{
                    border: 0,
                    background: "none",
                    color: "#2563eb",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 12.5,
                    padding: 0,
                  }}
                >
                  그대로 보기
                </button>
              </div>
            </div>
          )}
          {!loading && chapters.length > 0 && (
            <div key={`ch-${lastQ}`} className="audit-fade-in">
              <div
                style={{
                  fontSize: 12,
                  color: "var(--audit-text-muted)",
                  marginBottom: 6,
                }}
              >
                매칭 류 ({chapters.length})
                {chapters.length > 3 && (
                  <span
                    style={{
                      marginLeft: 6,
                      color: "var(--audit-text-faint)",
                      fontSize: 12,
                    }}
                  >
                    — 결과 분산. 검색어 더 구체화 (공백 분리, 따옴표, -단어)
                  </span>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {chapters.map(({ ch, count, sample_ko, sample_en }) => {
                  const active = ch === currentChapter;
                  // 매칭 token highlight — backend match_tokens (한글 입력 + 영문 변환)
                  // 둘 다 포함. fallback 으로 사용자 입력 token 도 합쳐 sample_en (영문)
                  // 의 한글 token 매칭 0건 case 안전망.
                  const qTokens = Array.from(
                    new Set(
                      [
                        ...(matchTokens || []),
                        ...q
                          .toLowerCase()
                          .replace(/[()[\]{}]/g, " ")
                          .split(/[\s,;·/]+/)
                          .filter((t) => t.length >= 2),
                      ].map((t) => String(t).toLowerCase()),
                    ),
                  );
                  return (
                    <button
                      key={ch}
                      onClick={() => navigateToChapter(ch)}
                      title={`${sample_ko || ""}\n${sample_en || ""}`.trim()}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: 2,
                        padding: "4px 8px",
                        border:
                          "1px solid " +
                          (active
                            ? "var(--audit-accent)"
                            : "var(--audit-border)"),
                        background: active
                          ? "var(--audit-accent-bg)"
                          : "var(--audit-bg)",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: 12,
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "JetBrains Mono, monospace",
                            fontWeight: 700,
                            color: active
                              ? "var(--audit-accent)"
                              : "var(--audit-text)",
                            minWidth: 30,
                          }}
                        >
                          {ch}
                        </span>
                        <span style={{ color: "var(--audit-text)" }}>
                          {chapterName(ch) || "—"}
                        </span>
                        <span
                          style={{
                            marginLeft: "auto",
                            fontSize: 12,
                            color: "var(--audit-text-faint)",
                            fontFamily: "JetBrains Mono, monospace",
                          }}
                        >
                          {count}
                        </span>
                      </div>
                      {(sample_ko || sample_en) && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--audit-text-faint)",
                            paddingLeft: 36,
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {highlightTokens(
                            sample_ko ||
                              (sample_en ? sample_en.toLowerCase() : ""),
                            qTokens,
                          )}
                          {sample_en && sample_ko && (
                            <span style={{ color: "var(--audit-text-muted)" }}>
                              {" / "}
                              {highlightTokens(sample_en, qTokens)}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {/* 검색매칭 하단 — 어떤 DB 어휘 source 로 hit 했는지 노출
                  (사용자 어휘 ↔ DB 어휘 추적용. 학명/외래어 fallback 시 특히 유용) */}
              <div
                style={{
                  marginTop: 6,
                  paddingTop: 6,
                  borderTop: "1px dashed var(--audit-border)",
                  fontSize: 12,
                  color: "var(--audit-text-faint)",
                  lineHeight: 1.4,
                }}
              >
                매칭 어휘:{" "}
                <span style={{ color: "var(--audit-text-muted)" }}>
                  {matchSourceLabel(matchSource)}
                </span>
                {(matchSource === "relaxed" || matchSource === "typo") && refinedTokens && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontWeight: 600,
                      color: "var(--audit-text)",
                    }}
                  >
                    재검색: {refinedTokens.join(" ")}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* 매칭 세번 나열 — 문의 "키워드 검색 결과 나열" (pineapple 검색 시
              류 칩만 나와 결과가 안 보이던 피드백). CLHS 관세율표 검색결과처럼
              호·소호 표제 매칭(2009.4 파인애플주스 등)도 함께. 행 클릭 → 상세. */}
          {!loading && (results.length > 0 || headingMatches.length > 0) && (
            <div key={`res-${lastQ}`} className="audit-fade-in">
              <div
                style={{
                  fontSize: 12,
                  color: "var(--audit-text-muted)",
                  margin: "8px 0 6px",
                }}
              >
                매칭 세번 ({headingMatches.length + results.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {headingMatches.map((h) => {
                  const code =
                    h.level === "heading"
                      ? `${h.code.slice(0, 2)}.${h.code.slice(2)}`
                      : `${h.code.slice(0, 4)}.${h.code.slice(4)}`;
                  // "08.04 - 대추야자…" / "- 파인애플주스" → 코드·대시 프리픽스 제거
                  const label = (h.line_ko || h.line_en || "").replace(
                    /^[\d.]*\s*[-–]\s*/,
                    "",
                  );
                  return (
                    <button
                      key={`${h.level}-${h.code}`}
                      onClick={() => navigateAuto({ hs: h.hs4 })}
                      title={`${h.line_ko || ""}\n${h.line_en || ""}`.trim()}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 8,
                        padding: "3px 8px",
                        border: "1px solid var(--audit-border)",
                        background: "var(--audit-bg-subtle)",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: 12,
                        textAlign: "left",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "JetBrains Mono, monospace",
                          fontWeight: 700,
                          color: "var(--audit-text)",
                          flexShrink: 0,
                        }}
                      >
                        {code}
                      </span>
                      <span
                        style={{
                          flexShrink: 0,
                          fontSize: 12,
                          color: "var(--audit-accent)",
                          border: "1px solid var(--audit-accent)",
                          borderRadius: 3,
                          padding: "0 4px",
                          lineHeight: 1.4,
                        }}
                      >
                        {h.level === "heading" ? "호" : "소호"}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "var(--audit-text-muted)",
                        }}
                      >
                        {label}
                      </span>
                    </button>
                  );
                })}
                {(resultsOpen ? results : results.slice(0, 12)).map((it) => {
                  const hskDigits = String(it.hsk || "").replace(/\D/g, "");
                  if (hskDigits.length < 10) return null;
                  const code = `${hskDigits.slice(0, 4)}.${hskDigits.slice(4, 6)}-${hskDigits.slice(6)}`;
                  const label = it.desc_ko || it.desc || "";
                  return (
                    <button
                      key={hskDigits}
                      onClick={() =>
                        navigateAuto({
                          hs: hskDigits.slice(0, 4),
                          hsk: hskDigits,
                        })
                      }
                      title={`${it.desc_ko || ""}\n${it.desc || ""}`.trim()}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 8,
                        padding: "3px 8px",
                        border: "1px solid var(--audit-border)",
                        background: "var(--audit-bg)",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: 12,
                        textAlign: "left",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "JetBrains Mono, monospace",
                          fontWeight: 700,
                          color: "var(--audit-text)",
                          flexShrink: 0,
                        }}
                      >
                        {code}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "var(--audit-text-muted)",
                        }}
                      >
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>
              {results.length > 12 && (
                <button
                  onClick={() => setResultsOpen((v) => !v)}
                  style={{
                    marginTop: 4,
                    border: "none",
                    background: "transparent",
                    color: "var(--audit-accent)",
                    fontSize: 12,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    padding: "2px 8px",
                  }}
                >
                  {resultsOpen ? "접기" : `전체 ${results.length}건 보기`}
                </button>
              )}
            </div>
          )}

          {/* 분류사례 + 관세사 사례 list 는 우측 EvidencePanel 에서 표시 (이중 노출 방지).
              좌측 = chapter chip + 매칭어휘만, 우측 = 사례 detail (넓고 풍부). */}

          {/* "구글" 체크 ON → Google 보강 (Gemini grounding) — 매칭 류 아래 (사용자 우선순위) */}
          {!loading && !err && googleHintOn && googleHint && (
            <div
              style={{
                padding: 10,
                fontSize: 12,
                color: "var(--audit-text)",
                background: "#dbeafe",
                borderRadius: 4,
                lineHeight: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontWeight: 600,
                  color: "#1a73e8",
                }}
              >
                Google 검색 보강
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--audit-text-muted)",
                    fontWeight: 400,
                  }}
                >
                  ({googleHint.query})
                </span>
              </div>
              {googleHint.summary && (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--audit-text)",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.6,
                  }}
                >
                  {googleHint.summary}
                </div>
              )}
              {googleHint.sources?.length > 0 && (
                <div style={{ fontSize: 12, color: "var(--audit-text-muted)" }}>
                  출처:{" "}
                  {googleHint.sources.slice(0, 3).map((s, i) => (
                    <span key={i}>
                      {i > 0 && " · "}
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#1a73e8", textDecoration: "none" }}
                      >
                        {s.title}
                      </a>
                    </span>
                  ))}
                </div>
              )}
              {googleHint.images?.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: 4,
                    overflowX: "auto",
                    paddingTop: 4,
                  }}
                >
                  {googleHint.images.slice(0, 6).map((img, i) => (
                    <a
                      key={i}
                      href={img.context_url || img.image_url}
                      target="_blank"
                      rel="noreferrer"
                      title={img.title}
                    >
                      <img
                        src={img.thumbnail_url}
                        alt={img.title}
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 3,
                          border: "1px solid var(--audit-border)",
                        }}
                      />
                    </a>
                  ))}
                </div>
              )}
              <div
                style={{
                  fontSize: 12,
                  color: "var(--audit-text-faint)",
                  marginTop: 2,
                }}
              >
                ⚠ HS 분류 결과 아님 — 외부 참고용. Google 에 검색어 전송됨.
              </div>
            </div>
          )}

          {/* PROVE 판정과 근거 그래프는 중앙 작업공간에서 표시한다. */}
        </>
      )}
    </div>
  );
}
